// IMAGE-PREVIEWS: which copy of a chat image each place asks for. The list shows the ~400 px
// thumbnail, the lightbox the ~1600 px preview, and Download / "open original" the file itself
// (no size). A file with no small copies (has_thumb false: not a raster image) is asked for whole.
import { type RefObject, useEffect, useState } from "react";

export type ImageVariant = "thumb" | "preview" | "full";
export type ImagePlace = "list" | "lightbox" | "download";

type Sized = {
  has_thumb: boolean;
  width?: number | null | undefined;
  height?: number | null | undefined;
};

/** The copy a place shows. */
export function variantFor(file: Pick<Sized, "has_thumb">, place: ImagePlace): ImageVariant {
  if (!file.has_thumb || place === "download") return "full";
  return place === "list" ? "thumb" : "preview";
}

/** The `size` query of a variant (the original has none). */
export const sizeQuery = (v: ImageVariant) => (v === "full" ? undefined : v);

/** width / height of an image whose size is stored, else null (the box then follows the image). */
export function aspectOf(file: Sized): number | null {
  const { width, height } = file;
  return width && height && width > 0 && height > 0 ? width / height : null;
}

/**
 * True once the element is within `margin` of the viewport (and for good after): a long chat
 * fetches the images in view and just below, not all of them. Without IntersectionObserver
 * (old browsers, tests) it is true at once.
 */
export function useNearViewport(ref: RefObject<Element | null>, margin = "400px"): boolean {
  const [near, setNear] = useState(typeof IntersectionObserver === "undefined");
  useEffect(() => {
    const el = ref.current;
    if (near || !el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setNear(true);
      },
      { rootMargin: margin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near, ref, margin]);
  return near;
}
